import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Box,
} from '@mui/material'
import { useAuth0 } from '@/utils/auth/auth0'
import { clearDatabaseSearchStorage } from '@/utils/databaseSearchSession'
import {
  fetchAuth0Identities,
  linkAuth0Identities,
  unLinkAuth0Identities,
} from '@/api/usersAPI'

type LoginMethod = 'google' | 'email_password'

interface Identity {
  provider: string
  connection: string
  user_id: string
  is_social: boolean
}

const availableMethods: {
  value: LoginMethod
  label: string
  provider: string
}[] = [
  { value: 'google', label: 'Google', provider: 'google-oauth2' },
  { value: 'email_password', label: 'Email & Password', provider: 'auth0' },
]

interface LinkAccountModalProps {
  showLinkAccountModal: boolean
  setShowLinkAccountModal: React.Dispatch<React.SetStateAction<boolean>>
  // onSuccess?: () => void
  token: string | null
}

export default function LinkAccountModal({
  showLinkAccountModal,
  setShowLinkAccountModal,
  token,
}: // onSuccess,
LinkAccountModalProps) {
  const { loginWithPopup, loginWithRedirect, getIdTokenClaims, user, logout } =
    useAuth0()

  const [identities, setIdentities] = useState<Identity[]>([])
  const [loadingIdentities, setLoadingIdentities] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [isLinking, setIsLinking] = useState(false)
  const [isLinked, setIsLinked] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)
  const [isUnLinked, setIsUnLinked] = useState(false)

  const [showAttemptedLoginPrompt, setShowAttemptedLoginPrompt] =
    useState(false)

  useEffect(() => {
    async function fetchIdentities() {
      if (!user?.sub || !token) return
      setLoadingIdentities(true)
      setError(null)
      try {
        const data = await fetchAuth0Identities(token, user?.sub)
        setIdentities(data)
      } catch (error) {
        setError('Error loading linked accounts')
      } finally {
        setLoadingIdentities(false)
      }
    }

    if (showLinkAccountModal) {
      fetchIdentities()
    }
  }, [showLinkAccountModal, user?.sub, isUnLinked, isLinked])

  const methodToLink = availableMethods.find(
    (method) => !identities.some((id) => id.provider === method.provider)
  )

  const currentIdentity = identities.find(
    (id) => `${id.provider}|${id.user_id}` === user?.sub
  )

  const currentMethod = availableMethods.find(
    (method) => method.provider === currentIdentity?.provider
  )

  const secondaryMethod = availableMethods.find(
    (method) =>
      method.provider ===
      identities.find((id) => id.provider !== currentMethod?.provider)?.provider
  )

  const secondaryAuth0Id = identities.find(
    (id) => id.provider !== currentMethod?.provider
  )?.user_id

  async function handleLinkAccount(screenHint?: 'signup' | 'login') {
    if (!methodToLink || !token) return

    setError(null)
    setIsLinking(true)

    try {
      const primaryAuth0Id = user?.sub
      if (!primaryAuth0Id) {
        throw new Error('Missing primary user identity')
      }

      if (methodToLink.value === 'google') {
        // Google flow: can be handled in popup
        await loginWithPopup({
          connection: 'google-oauth2',
          prompt: 'login',
          login_hint: user?.email,
        })

        const secondaryIdToken = await getIdTokenClaims()
        const secondaryAuth0Id = secondaryIdToken?.sub
        const secondaryEmail = secondaryIdToken?.email?.toLowerCase()
        const secondaryEmailVerified = secondaryIdToken?.email_verified

        if (!secondaryAuth0Id || !secondaryEmail) {
          throw new Error('Could not identify secondary account or email')
        }

        if (!secondaryEmailVerified) {
          throw new Error('Google account email is not verified')
        }

        const primaryEmail = user?.email?.toLowerCase()

        if (!primaryEmail || secondaryEmail !== primaryEmail) {
          throw new Error('Emails do not match — cannot link these accounts')
        }

        await linkAuth0Identities(token, primaryAuth0Id, secondaryAuth0Id)

        setIsLinked(true)

        // Force logout + login to switch to primary identity
        clearDatabaseSearchStorage()
        await logout({ returnTo: window.location.origin })
      } else {
        // Email/password flow: must be handled in redirect, linking handled in useHandleAccountLinking()
        await loginWithRedirect({
          connection: 'Username-Password-Authentication',
          prompt: 'login',
          login_hint: user.email,
          screen_hint: screenHint, // 'login' or 'signup' depending on if the user has attempted log in before linking
          appState: {
            linkAccount: true,
            primaryAuth0Id,
            primaryEmail: user.email,
            token,
          },
        })
      }
    } catch (err: unknown) {
      setError('Sorry, an error occurred during linking.')
    } finally {
      setIsLinking(false)
    }
  }

  async function handleRemoveSecondaryLogin() {
    if (!secondaryAuth0Id || !secondaryMethod || !token) return

    setError(null)
    setIsUnlinking(true)

    try {
      const primaryAuth0Id = user?.sub
      if (!primaryAuth0Id) {
        throw new Error('Missing primary user identity')
      }

      await unLinkAuth0Identities(
        token,
        primaryAuth0Id,
        secondaryMethod?.provider,
        secondaryAuth0Id
      )

      setIsUnLinked(true)
    } catch (err: unknown) {
      setError('Sorry, an error occurred during unlinking.')
    } finally {
      setIsUnlinking(false)
    }
  }

  return (
    <>
      <Dialog
        open={showLinkAccountModal}
        onClose={() => setShowLinkAccountModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ textAlign: 'center', fontSize: '24px' }}>
          Manage Login Methods
        </DialogTitle>

        {loadingIdentities ? (
          <DialogContent sx={{ width: '100%', textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#1C3045' }} />
          </DialogContent>
        ) : (
          <>
            <DialogContent sx={{ width: '100%', textAlign: 'center' }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, width: '80%', mx: 'auto' }}
              >
                Adding another login method allows you to sign in using
                different providers while keeping your data in a single account.
              </Typography>

              {methodToLink ? (
                <>
                  <Typography variant="body1" sx={{ mt: 4, mb: 1 }}>
                    You are currently signed in with{' '}
                    <strong>{currentMethod?.label}</strong>
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 4 }}>
                    You can add a login method so that you're also able to sign
                    in with <strong>{methodToLink.label}</strong>
                  </Typography>

                  <Button
                    disableRipple
                    variant="contained"
                    onClick={() => {
                      if (methodToLink.value === 'email_password') {
                        setShowAttemptedLoginPrompt(true) // if the user has already signed in, but got a 409 they're still registered in auth0 so we need to show the login form instead of signup form
                      } else {
                        handleLinkAccount()
                      }
                    }}
                    disabled={isLinking || isLinked || showAttemptedLoginPrompt}
                    startIcon={
                      isLinking ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : undefined
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#1C3045',
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '16px',
                      boxShadow: 'none',
                    }}
                  >
                    {isLinking
                      ? 'Linking...'
                      : `Add ${methodToLink.label} Login`}
                  </Button>

                  <Alert
                    severity="info"
                    icon={false}
                    sx={{
                      mt: 2,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      Please ensure that the email address you use as an
                      alternative login matches the one you are currently signed
                      in with. Accounts can only be linked with the same email
                      address.
                    </span>{' '}
                    <br /> After adding a login method you will be prompted to
                    sign in again to complete the process.
                  </Alert>

                  {showAttemptedLoginPrompt && (
                    <Box mt={4} textAlign="center">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Quick question so we can redirect you to the right
                        page...
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Have you already attempted to log in with{' '}
                        <span style={{ fontWeight: 500 }}>{user?.email}</span>{' '}
                        using email & password?
                      </Typography>
                      <Box display="flex" justifyContent="center" gap={2}>
                        <Button
                          disableRipple
                          variant="contained"
                          onClick={() => handleLinkAccount('signup')}
                          sx={{ textTransform: 'none' }}
                        >
                          No – Create new account
                        </Button>
                        <Button
                          variant="outlined"
                          disableRipple
                          onClick={() => handleLinkAccount('login')}
                          sx={{
                            textTransform: 'none',
                            '&:focus': {
                              backgroundColor: '#e8e8e8',
                            },
                          }}
                        >
                          Yes – I’ve tried to login
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ my: 4, fontWeight: 500 }}>
                    You have already linked all available login methods.
                  </Typography>
                  <Typography>
                    If you'd like to remove the secondary login method, click
                    the button below.
                  </Typography>

                  <Button
                    disableRipple
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#1C3045',
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '16px',
                      boxShadow: 'none',
                      mt: 2,
                    }}
                    onClick={handleRemoveSecondaryLogin}
                  >
                    {isUnlinking
                      ? 'Unlinking...'
                      : `Remove ${secondaryMethod?.label} Login`}
                  </Button>
                  <Alert
                    severity="warning"
                    sx={{
                      my: 2,
                      textAlign: 'center',
                      mx: 'auto',
                      width: '100%',
                    }}
                  >
                    Removing the secondary login means you will no longer be
                    able to sign in to this account with{' '}
                    <span style={{ fontWeight: 500 }}>
                      {secondaryMethod?.label}
                    </span>
                    . <br />
                    You will only be able to sign in with{' '}
                    <span style={{ fontWeight: 500 }}>
                      {currentMethod?.label}
                    </span>
                    .
                  </Alert>
                </>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {isLinked && (
                <Alert severity="success" sx={{ my: 2 }}>
                  Accounts linked successfully! You can now log in using either
                  method. Please wait, you will be redirected to the login
                  page...
                </Alert>
              )}
              {isUnLinked && (
                <Alert severity="success" sx={{ my: 2 }}>
                  Secondary login removed successfully!
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                disableRipple
                onClick={() => setShowLinkAccountModal(false)}
                sx={{
                  mr: 1,
                  textTransform: 'none',
                  color: '#1C3045',
                  fontSize: '16px',
                  borderColor: '#1C3045',
                  border: 2,
                  borderRadius: 2,
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                {isLinked || isUnLinked ? 'Close' : 'Cancel'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}
